import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { MenuIcon, XIcon, WalletIcon, PlaneIcon, HistoryIcon, HomeIcon, SparklesIcon } from 'lucide-react';
import { ROUTES } from '../../constants/routes';
import { Button } from '../ui/Button';
import { NotificationCenter } from '../features/NotificationCenter';
export interface HeaderProps {
  walletBalance?: number;
}
// Animated counter for wallet balance
function AnimatedBalance({
  value
}: {
  value: number;
}) {
  const [displayValue, setDisplayValue] = useState(value);
  useEffect(() => {
    const duration = 500;
    const startValue = displayValue;
    const startTime = Date.now();
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      setDisplayValue(Math.floor(startValue + (value - startValue) * easeOut));
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }, [value]);
  return <span className="tabular-nums" data-id="element-320">₦{displayValue.toLocaleString()}</span>;
}
export function Header({
  walletBalance
}: HeaderProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const {
    scrollY
  } = useScroll();
  // Header background blur on scroll
  const headerBg = useTransform(scrollY, [0, 50], ['rgba(255, 255, 255, 0.8)', 'rgba(255, 255, 255, 0.95)']);
  const headerBlur = useTransform(scrollY, [0, 50], [0, 12]);
  const headerShadow = useTransform(scrollY, [0, 50], ['0 0 0 rgba(0,0,0,0)', '0 4px 20px rgba(0,0,0,0.08)']);
  const navItems = [{
    label: 'Home',
    path: ROUTES.HOME,
    icon: HomeIcon
  }, {
    label: 'Wallet',
    path: ROUTES.WALLET,
    icon: WalletIcon
  }, {
    label: 'Book Flight',
    path: ROUTES.SEARCH,
    icon: PlaneIcon
  }, {
    label: 'History',
    path: ROUTES.HISTORY,
    icon: HistoryIcon
  }];
  const isActive = (path: string) => location.pathname === path;
  // Check if user is logged in (mock - in real app would check auth state)
  const isLoggedIn = walletBalance !== undefined;
  return <motion.header style={{
    backgroundColor: headerBg,
    backdropFilter: useTransform(headerBlur, v => `blur(${v}px)`),
    boxShadow: headerShadow
  }} className="sticky top-0 z-40 border-b border-surface-200/50" data-id="element-321">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-322">
        <div className="flex items-center justify-between h-16" data-id="element-323">
          {/* Logo */}
          <Link to={ROUTES.HOME} className="flex items-center gap-2 group" data-id="element-324">
            <motion.div className="w-9 h-9 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/20" whileHover={{
            scale: 1.05,
            rotate: -5
          }} whileTap={{
            scale: 0.95
          }} data-id="element-325">
              <PlaneIcon className="w-5 h-5 text-white" data-id="element-326" />
            </motion.div>
            <span className="text-heading-md font-bold text-surface-900 group-hover:text-primary-600 transition-colors" data-id="element-327">
              AeroZeep
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1" data-id="element-328">
            {navItems.map(item => <Link key={item.path} to={item.path} className="relative px-4 py-2 text-body-md font-medium transition-colors" data-id="element-329">
                <span className={isActive(item.path) ? 'text-primary-600' : 'text-surface-600 hover:text-surface-900'} data-id="element-330">
                  {item.label}
                </span>
                {isActive(item.path) && <motion.div layoutId="nav-indicator" className="absolute inset-0 bg-primary-50 rounded-lg -z-10" transition={{
              type: 'spring',
              stiffness: 500,
              damping: 30
            }} data-id="element-331" />}
              </Link>)}
          </nav>

          {/* Right Side */}
          <div className="flex items-center gap-3" data-id="element-332">
            {/* Notification Center (only if logged in) */}
            {isLoggedIn && <NotificationCenter data-id="element-333" />}

            {/* Wallet Balance (only if logged in) */}
            {isLoggedIn && walletBalance !== undefined && <Link to={ROUTES.WALLET} data-id="element-334">
                <motion.div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-surface-100 to-surface-50 rounded-lg border border-surface-200/50 hover:border-primary-200 hover:bg-primary-50/50 transition-all" whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} data-id="element-335">
                  <div className="w-5 h-5 bg-primary-100 rounded flex items-center justify-center" data-id="element-336">
                    <WalletIcon className="w-3 h-3 text-primary-600" data-id="element-337" />
                  </div>
                  <span className="text-body-sm font-semibold text-surface-900" data-id="element-338">
                    <AnimatedBalance value={walletBalance} data-id="element-339" />
                  </span>
                </motion.div>
              </Link>}

            {/* Auth Buttons (only if not logged in) */}
            {!isLoggedIn && <>
                <Link to={ROUTES.LOGIN} className="hidden sm:block" data-id="element-340">
                  <Button variant="ghost" size="sm" data-id="element-341">
                    Sign In
                  </Button>
                </Link>
                <Link to={ROUTES.SIGNUP} className="hidden sm:block" data-id="element-342">
                  <Button variant="secondary" size="sm" data-id="element-343">
                    Sign Up
                  </Button>
                </Link>
              </>}

            {/* Get Refund CTA */}
            <Link to={ROUTES.REFUND} data-id="element-344">
              <motion.div whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} data-id="element-345">
                <Button variant="primary" size="sm" className="shadow-lg shadow-primary-500/20" icon={<SparklesIcon className="w-4 h-4" data-id="element-347" />} data-id="element-346">
                  Get Refund
                </Button>
              </motion.div>
            </Link>

            {/* Mobile Menu Button */}
            <motion.button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="md:hidden p-2 text-surface-600 hover:text-surface-900 hover:bg-surface-100 rounded-lg transition-colors" aria-label="Toggle menu" whileTap={{
            scale: 0.9
          }} data-id="element-348">
              <AnimatePresence mode="wait" data-id="element-349">
                {isMobileMenuOpen ? <motion.div key="close" initial={{
                rotate: -90,
                opacity: 0
              }} animate={{
                rotate: 0,
                opacity: 1
              }} exit={{
                rotate: 90,
                opacity: 0
              }} transition={{
                duration: 0.15
              }} data-id="element-350">
                    <XIcon className="w-6 h-6" data-id="element-351" />
                  </motion.div> : <motion.div key="menu" initial={{
                rotate: 90,
                opacity: 0
              }} animate={{
                rotate: 0,
                opacity: 1
              }} exit={{
                rotate: -90,
                opacity: 0
              }} transition={{
                duration: 0.15
              }} data-id="element-352">
                    <MenuIcon className="w-6 h-6" data-id="element-353" />
                  </motion.div>}
              </AnimatePresence>
            </motion.button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence data-id="element-354">
        {isMobileMenuOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.2,
        ease: 'easeInOut'
      }} className="md:hidden border-t border-surface-200 bg-white/95 backdrop-blur-lg overflow-hidden" data-id="element-355">
            <nav className="px-4 py-4 space-y-1" data-id="element-356">
              {navItems.map((item, index) => {
            const Icon = item.icon;
            return <motion.div key={item.path} initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: index * 0.05
            }} data-id="element-357">
                    <Link to={item.path} onClick={() => setIsMobileMenuOpen(false)} className={`
                        flex items-center gap-3 px-4 py-3 rounded-xl text-body-md font-medium transition-all
                        ${isActive(item.path) ? 'bg-primary-50 text-primary-600 shadow-sm' : 'text-surface-600 hover:bg-surface-50 active:bg-surface-100'}
                      `} data-id="element-358">
                      <Icon className="w-5 h-5" data-id="element-359" />
                      {item.label}
                    </Link>
                  </motion.div>;
          })}

              {/* Mobile Auth Links (only if not logged in) */}
              {!isLoggedIn && <motion.div initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: navItems.length * 0.05
          }} className="pt-3 mt-3 border-t border-surface-200 space-y-2" data-id="element-360">
                  <Link to={ROUTES.LOGIN} onClick={() => setIsMobileMenuOpen(false)} className="block px-4 py-3 text-center bg-surface-50 hover:bg-surface-100 rounded-xl text-body-md font-medium text-surface-700 transition-colors" data-id="element-361">
                    Sign In
                  </Link>
                  <Link to={ROUTES.SIGNUP} onClick={() => setIsMobileMenuOpen(false)} className="block px-4 py-3 text-center bg-primary-500 hover:bg-primary-600 rounded-xl text-body-md font-medium text-white transition-colors shadow-lg shadow-primary-500/20" data-id="element-362">
                    Sign Up
                  </Link>
                </motion.div>}

              {/* Mobile Wallet Balance (only if logged in) */}
              {isLoggedIn && walletBalance !== undefined && <motion.div initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: navItems.length * 0.05
          }} className="pt-3 mt-3 border-t border-surface-200" data-id="element-363">
                  <Link to={ROUTES.WALLET} onClick={() => setIsMobileMenuOpen(false)} className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-primary-50 to-accent-50 rounded-xl" data-id="element-364">
                    <div className="flex items-center gap-3" data-id="element-365">
                      <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center" data-id="element-366">
                        <WalletIcon className="w-5 h-5 text-primary-600" data-id="element-367" />
                      </div>
                      <span className="text-body-md font-medium text-surface-700" data-id="element-368">
                        Your Balance
                      </span>
                    </div>
                    <span className="text-heading-md font-bold text-primary-600 tabular-nums" data-id="element-369">
                      ₦{walletBalance.toLocaleString()}
                    </span>
                  </Link>
                </motion.div>}
            </nav>
          </motion.div>}
      </AnimatePresence>
    </motion.header>;
}